import api from '../../api/axiosConfig';
import {
  primeExtensionCredentials,
  primeVideoTargetReliable,
  serializeExtensionOpen,
  isExtensionInstalled,
} from './instatExtension';

// Open a new tab immediately on user click (avoid popup-blocker) then navigate it
// once the backend returns the Hudl playlist link. If the backend errors or the
// user's Hudl session is down, we rewrite the popup's body to show the error.

function openLoadingPopup(title) {
  const popup = window.open('about:blank', '_blank');
  if (!popup) return null;
  try {
    popup.document.title = title || 'Instat video…';
    popup.document.body.style.cssText =
      'margin:0;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display","Inter",sans-serif;color:#1D1D1F;background:#F5F5F7;';
    popup.document.body.innerHTML = `
      <div style="max-width:460px;margin:8rem auto;padding:2.5rem;background:#fff;border-radius:20px;box-shadow:0 1px 2px rgba(17,24,39,.04),0 8px 24px rgba(17,24,39,.05);text-align:center">
        <div style="width:44px;height:44px;margin:0 auto 1.25rem;border:3px solid rgba(0,0,0,.08);border-top-color:#007AFF;border-radius:50%;animation:spin .9s cubic-bezier(.25,.1,.25,1) infinite"></div>
        <div style="font-size:18px;font-weight:600;letter-spacing:-.01em;margin-bottom:.35rem">Loading Hudl playlist…</div>
        <div style="font-size:13.5px;color:#86868B">${title || ''}</div>
      </div>
      <style>@keyframes spin{to{transform:rotate(360deg)}}</style>`;
  } catch {
    /* some browsers block document.write on about:blank */
  }
  return popup;
}

function writePopupError(popup, message) {
  if (!popup) return;
  try {
    popup.document.body.innerHTML = `
      <div style="max-width:540px;margin:6rem auto;padding:2.5rem;background:#fff;border-radius:20px;box-shadow:0 1px 2px rgba(17,24,39,.04),0 8px 24px rgba(17,24,39,.05);font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif;color:#1D1D1F">
        <div style="font-size:20px;font-weight:700;letter-spacing:-.015em;color:#FF3B30;margin-bottom:1rem">Couldn't load the playlist</div>
        <pre style="white-space:pre-wrap;background:#FEF2F2;padding:1rem 1.25rem;border-radius:12px;font-size:12.5px;color:#86868B;font-family:ui-monospace,Menlo,Consolas,monospace;margin:0">${String(message).slice(0, 2000)}</pre>
        <div style="font-size:12.5px;color:#86868B;margin-top:1.25rem">You can close this tab.</div>
      </div>`;
  } catch {}
}

async function fetchAndOpen(endpoint, body, popupTitle, playerContext = null) {
  const popup = openLoadingPopup(popupTitle);
  if (!popup) {
    alert('Le navigateur a bloqué le pop-up. Autorise les pop-ups sur cette page puis réessaie.');
    return;
  }
  // Sérialisé : une seule ouverture mène sa cible jusqu'à popup.location avant
  // que la suivante ne prime la sienne (cible globale unique côté extension).
  return serializeExtensionOpen(async () => {
    // Prime the extension in parallel so that if the Hudl popup lands on the
    // login page, the autologin content script has credentials ready.
    primeExtensionCredentials().catch(() => null);
    try {
      const { data } = await api.post(`/api/instat/video/${endpoint}`, body);
      if (data?.link) {
        // Prime the target URL so the extension can bring the popup back here
        // after OAuth login if Hudl drops the user on a landing page instead.
        // playerContext (when present) lets the extension tag every clip saved
        // from this Hudl tab with our local player_id + instat_player_id.
        if (isExtensionInstalled()) {
          await primeVideoTargetReliable(data.link, playerContext);
        }
        popup.location = data.link;
      } else {
        writePopupError(popup, `Pas de lien retourné. Réponse : ${JSON.stringify(data)}`);
      }
    } catch (e) {
      const detail = e.response?.data?.error || e.response?.data || e.message;
      writePopupError(popup, typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2));
    }
  });
}

export function openSkillVideo({ playerId, playerName, skill, playerContext = null }) {
  return fetchAndOpen(
    'skill',
    {
      playerId,
      skillId: skill.skill_id,
      title: playerName || `Player ${playerId}`,
      subtitle: skill.label,
    },
    `${playerName || playerId} · ${skill.label}`,
    playerContext,
  );
}

export function openMatchVideo({ playerId, playerName, match, playlistType, label, playerContext = null }) {
  // Matches from the Games tab expose `team` / `opp_team`; the old MatchesTable
  // uses `team1` / `team2`. Support both shapes.
  const home = match.team || match.team1;
  const away = match.opp_team || match.team2;
  return fetchAndOpen(
    'match',
    {
      playerId,
      matchId: match.id,
      playlistType,
      title: playerName || `Player ${playerId}`,
      subtitle: `${home?.name || ''} ${home?.score ?? ''}-${away?.score ?? ''} ${away?.name || ''} · ${label || playlistType}`,
    },
    `${playerName || playerId} · ${label || playlistType}`,
    playerContext,
  );
}

export function openStatVideo({ playerId, playerName, stat, matchIds, seasonLabel, playerContext = null }) {
  return fetchAndOpen(
    'stat',
    {
      playerId,
      matchIds,
      statparam: stat.statparam,
      statoption: stat.statoption ?? 0,
      title: playerName || `Player ${playerId}`,
      subtitle: `${stat.label}${seasonLabel ? ' · ' + seasonLabel : ''}`,
    },
    `${playerName || playerId} · ${stat.label}`,
    playerContext,
  );
}

// Career-scoped stat video. We don't have match IDs for that season yet —
// open popup synchronously, fetch games for the season, then call /video/stat.
export async function openCareerStatVideo({
  playerId,
  playerName,
  seasonId,
  seasonLabel,
  stat, // { statparam, label, ... }
  playerContext = null,
}) {
  const popupTitle = `${playerName || playerId} · ${stat.label}${seasonLabel ? ' · ' + seasonLabel : ''}`;
  const popup = openLoadingPopup(popupTitle);
  if (!popup) {
    alert('The browser blocked the pop-up. Allow pop-ups on this page then try again.');
    return;
  }
  // Sérialisé comme fetchAndOpen : ce flux a DEUX allers-retours réseau avant le
  // prime (GET games puis POST stat), donc la fenêtre de chevauchement avec un
  // autre clic est la plus large — c'est ici que la sérialisation compte le plus.
  return serializeExtensionOpen(async () => {
    primeExtensionCredentials().catch(() => null);
    try {
      const { data: games } = await api.get(
        `/api/instat/player/${playerId}/games?seasonId=${seasonId}`,
      );
      const matchIds = (games?.matches || []).map((m) => m.id);
      if (!matchIds.length) {
        writePopupError(popup, 'No matches found for this season.');
        return;
      }
      const { data } = await api.post('/api/instat/video/stat', {
        playerId,
        matchIds,
        statparam: stat.statparam,
        statoption: stat.statoption ?? 0,
        title: playerName || `Player ${playerId}`,
        subtitle: `${stat.label}${seasonLabel ? ' · ' + seasonLabel : ''}`,
      });
      if (data?.link) {
        if (isExtensionInstalled()) {
          await primeVideoTargetReliable(data.link, playerContext);
        }
        popup.location = data.link;
      } else {
        writePopupError(popup, `No link returned. Response: ${JSON.stringify(data)}`);
      }
    } catch (e) {
      const detail = e.response?.data?.error || e.response?.data || e.message;
      writePopupError(popup, typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2));
    }
  });
}
